// ─── PlansPage — Account → View Plans ─────────────────────────────────────────
// "Your Project Plans" — one card per project the signed-in user is
// part of. Each card shows:
//   Project (left)   | Included in Your Plan (middle) | Billing and payment (right)
//
// Feature lists come from GET /api/account/tiers/ (a public read of
// AccountType rows). Clicking "+N View More" opens the full list in a
// popup; clicking "Manage Plan" opens the four-action Manage Plan
// popup (Change Plan / Change Commitment / Manage Add-Ons / Cancel).

import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Loader, AlertCircle, CreditCard, Calendar, ChevronRight, X, Check } from 'lucide-react'
import { Navigation } from '../components/Navigation'
import { AppHeader } from '../components/AppHeader'
import { projectsApi } from '../lib/projectsApi'
import useAuthStore from '../store/auth-store'

// Storage + support items the AccountType table doesn't own (they're
// legacy Symfony copy that lived on the tier admin page) — merged in
// per-tier so the "Included in Your Plan" list matches the reference
// design. Kept small so the marketing team can iterate without a
// migration.
const EXTRA_TIER_ITEMS = {
  Basic: [
    '1-Year Data Storage',
    'Graph, Statistics, Tables',
    'Dashboards',
    'Remote Configuration',
  ],
  Advanced: [
    'Unlimited Data Storage',
    'Field Notes',
    'Streaming Methods',
    'Secure Cloud',
    'Interactive Map',
    'Multi-level Users',
  ],
  Professional: [
    'Unlimited Data Storage',
    '10GB Photo and Video Storage',
    'Field Notes',
    'Streaming Methods',
    'Priority Support',
    'Secure Cloud',
    'Interactive Map',
    'Multi-level Users',
  ],
  Enterprise: [
    'Unlimited Data Storage',
    'Unlimited Photo and Video Storage',
    'Field Notes',
    'Streaming Methods',
    'Dedicated Support',
    'SSO',
    'Secure Cloud',
    'Interactive Map',
    'Multi-level Users',
  ],
}

// Merge server tiers with the marketing copy so `includedFor(name)`
// returns a single ordered list of strings to render.
function featuresFor(tierName, tiers) {
  const t     = tiers.find((x) => x.name === tierName) || {}
  const flags = (t.features || []).filter((f) => f.included).map((f) => f.label)
  const extra = EXTRA_TIER_ITEMS[tierName] || []
  // De-dup while preserving order (extra items first — they're the
  // storage / support headline items in the reference design).
  const seen = new Set(), out = []
  for (const s of [...extra, ...flags]) {
    if (!seen.has(s)) { seen.add(s); out.push(s) }
  }
  return out
}

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return String(iso)
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d)
  } catch { return d.toDateString() }
}
function fmtMoney(amount, currency = 'USD') {
  if (amount == null) return ''
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 })
      .format(Number(amount))
  } catch { return `$${Number(amount).toFixed(2)}` }
}

// ─── "Included in Your Plan" popup ────────────────────────────────────────────
// Renders every feature for a tier as a bulleted list. Triggered from
// "+N View More" links (both on the main page and inside the Manage
// Plan modal).
function IncludedFeaturesModal({ open, tierName, features, onClose }) {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="common-size15 font-bold text-[#4D4D4D]">Included in Your Plan</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto text-sm">
          {tierName && (
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">{tierName}</p>
          )}
          <ul className="space-y-1.5 text-gray-800 font-semibold">
            {(features || []).map((f) => (
              <li key={f}>- {f}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

// ─── "Manage Plan" popup ──────────────────────────────────────────────────────
// Layout matches the XD:
//   Left column   — Project + My Plan + Included features (preview + View More)
//                   + My Add-Ons list.
//   Right column  — three action cards stacked vertically, each with a
//                   title, one-line description, and a primary button
//                   in the top-right (Change My Plan, Manage My Add-Ons,
//                   Cancel My Plan). Change Plan is the only wired one;
//                   the others alert "coming soon" until the billing
//                   endpoints land.
function ManagePlanModal({ open, project, tiers, onClose, onShowFeatures, onChangePlan, onPlanCancelled }) {
  const [addOnsOpen, setAddOnsOpen]   = useState(false)
  const [cancelOpen, setCancelOpen]   = useState(false)

  if (!open || !project) return null
  const tierName = project.plan?.name || project.plan_name || 'Basic'
  const features = featuresFor(tierName, tiers)
  const preview  = features.slice(0, 5)
  const rest     = Math.max(0, features.length - preview.length)
  // Real add-on list from the backend — each entry is an object with
  // {id, label, status, period, ...}. Fall back to a legacy string
  // list on older API responses so nothing breaks mid-deploy.
  const addOns = Array.isArray(project.plan?.add_ons) ? project.plan.add_ons : []

  const cards = [
    {
      title: 'Change My Plan',
      desc:  'Need additional capabilities for your project? Change your plan here.',
      btn:   'Change Plan',
      onClick: () => onChangePlan?.(project),
    },
    {
      title: 'Manage My Add-Ons',
      desc:  'Need additional features not included in your plan? Add on here.',
      btn:   'Manage Add-Ons',
      onClick: () => setAddOnsOpen(true),
    },
    {
      title: 'Cancel My Plan',
      desc:  'No longer need an upgraded plan? Cancel here.',
      btn:   'Cancel Plan',
      onClick: () => setCancelOpen(true),
    },
  ]

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Manage Plan</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[260px,minmax(0,1fr)] gap-6 px-6 py-5 overflow-y-auto overflow-x-hidden">
          {/* Left column — Project / Plan / Included / Add-Ons */}
          <div className="text-sm space-y-4">
            <div>
              <div className="common-size15 font-bold text-[#4D4D4D] mb-0.5">Project:</div>
              <div className="common-size15 font-semibold text-[#4D4D4D]">{project.name}</div>
            </div>

            <div>
              <div className="common-size15 font-bold text-[#4D4D4D] mb-0.5">My Plan:</div>
              <div className="text-gray-700">{tierName}</div>
            </div>

            <div>
              <div className="common-size15 font-bold text-[#4D4D4D] mb-1">Included in My Plan</div>
              <ul className="space-y-0.5 text-gray-700">
                {preview.map((f) => <li key={f}>{f}</li>)}
              </ul>
              {rest > 0 && (
                <button
                  type="button"
                  onClick={() => onShowFeatures?.({ tierName, features })}
                  className="mt-1 text-sm text-[#358ECD] hover:underline"
                >
                  +{rest} View More
                </button>
              )}
            </div>

            <div>
              <div className="common-size15 font-bold text-[#4D4D4D] mb-1">My Add-Ons:</div>
              {addOns.length ? (
                <ul className="space-y-0.5 text-gray-700">
                  {addOns.map((a, i) => {
                    const label = typeof a === 'string' ? a : (a.label || a.product_key || 'Add-on')
                    const key   = typeof a === 'string' ? `${a}-${i}` : a.id
                    return <li key={key}>- {label}</li>
                  })}
                </ul>
              ) : (
                <div className="text-gray-500 italic text-xs">No add-ons</div>
              )}
            </div>
          </div>

          {/* Right column — three larger action cards */}
          <div className="space-y-4">
            {cards.map((card) => (
              <div
                key={card.title}
                className="border border-gray-200 rounded-lg px-5 py-4 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="text-base font-bold text-gray-800 mb-1">{card.title}</div>
                  <div className="text-sm text-gray-600">{card.desc}</div>
                </div>
                <button
                  type="button"
                  onClick={card.onClick}
                  className="shrink-0 px-4 py-2 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded"
                >
                  {card.btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {addOnsOpen && (
        <ManageAddOnsModal project={project} onClose={() => setAddOnsOpen(false)} />
      )}
      {cancelOpen && (
        <CancelPlanModal
          project={project}
          onClose={() => setCancelOpen(false)}
          onCancelled={(payload) => {
            setCancelOpen(false)
            onPlanCancelled?.(project.id, payload)
            onClose?.()
          }}
        />
      )}
    </div>
  )
}

// ─── "Manage Add-Ons" modal ──────────────────────────────────────────────────
// Lists active cellular data plans (from /api/account/tiers/) with a
// Monthly/Annual toggle. Selecting one and clicking Continue posts to
// /subscriptions/checkout-addon/ and redirects to Stripe Checkout.
function ManageAddOnsModal({ project, onClose }) {
  const [plans,   setPlans]   = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const [period,  setPeriod]  = useState('monthly')
  const [busyKey, setBusyKey] = useState(null)

  useEffect(() => {
    let cancelled = false
    projectsApi.fetchTiers()
      .then((data) => { if (!cancelled) setPlans(Array.isArray(data?.cellular_plans) ? data.cellular_plans : []) })
      .catch((e)   => { if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load add-ons') })
      .finally(()  => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Map a cellular plan's `name` → the backend product_key convention
  // (`cellular_20mb`, `cellular_100mb`, …). Falls back to a slug from
  // the name so new plans work without a code change.
  const productKeyOf = (plan) => {
    if (!plan) return ''
    if (typeof plan.product_key === 'string' && plan.product_key) return plan.product_key
    const mb   = Number(plan.data_mb) || 0
    if (mb > 0) return `cellular_${mb}mb`
    return `cellular_${String(plan.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`
  }

  const buy = async (plan) => {
    setError(null); setBusyKey(plan.id)
    try {
      const res = await projectsApi.startAddonCheckout({
        projectId:  project.id,
        productKey: productKeyOf(plan),
        period,
      })
      if (res?.url) { window.location.assign(res.url); return }
      setError('Checkout session created but no redirect URL — contact support.')
    } catch (e) {
      const data = e?.response?.data || {}
      setError(data.code === 'price_id_missing'
        ? data.message
        : (data.message || e?.message || 'Add-on checkout failed'))
    } finally { setBusyKey(null) }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-800">Manage Add-Ons</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4">
          <p className="text-xs text-gray-600 mb-3">
            Add a cellular data plan to <strong>{project.name}</strong>. Every tier — including Basic — can buy these for the project.
          </p>

          {/* Monthly / Annual toggle */}
          <div className="flex items-center gap-1 mb-3 bg-gray-100 rounded p-1 w-fit">
            {['monthly', 'annual'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs font-semibold rounded transition capitalize ${
                  period === p ? 'bg-white shadow text-[#358ECD]' : 'text-gray-600 hover:text-gray-800'
                }`}
              >{p}{p === 'annual' ? ' (save ~10%)' : ''}</button>
            ))}
          </div>

          {loading && <div className="py-6 text-center text-sm text-gray-500 inline-flex items-center gap-2"><Loader className="w-4 h-4 animate-spin" /> Loading add-ons…</div>}
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}
          {!loading && plans.length === 0 && !error && (
            <p className="text-sm text-gray-500 italic">No add-ons available at this time.</p>
          )}

          <div className="space-y-2">
            {plans.map((plan) => {
              const price = period === 'annual' ? plan.price_annual : plan.price_monthly
              return (
                <div key={plan.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="common-size15 text-sm font-bold text-[#4D4D4D]">{plan.name}</div>
                    <div className="text-xs text-gray-500">
                      {plan.data_mb ? `${plan.data_mb} MB · ` : ''}{fmtMoney(price)} / {period === 'annual' ? 'year' : 'month'}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={busyKey === plan.id}
                    onClick={() => buy(plan)}
                    className="shrink-0 px-3 py-1.5 bg-[#358ECD] hover:bg-[#2A78B0] disabled:bg-gray-300 text-white text-sm font-semibold rounded inline-flex items-center gap-1"
                  >
                    {busyKey === plan.id && <Loader className="w-3.5 h-3.5 animate-spin" />}
                    Buy
                  </button>
                </div>
              )
            })}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="text-sm text-[#358ECD] hover:underline">Close</button>
        </div>
      </div>
    </div>
  )
}

// ─── "Cancel My Plan" modal ──────────────────────────────────────────────────
// End-of-period cancellation: the subscription keeps running until the
// current billing period ends, then reverts to Basic. No refund.
function CancelPlanModal({ project, onClose, onCancelled }) {
  const [busy,   setBusy]   = useState(false)
  const [error,  setError]  = useState(null)
  const [done,   setDone]   = useState(null)  // response payload on success

  const confirm = async () => {
    setBusy(true); setError(null)
    try {
      const res = await projectsApi.cancelSubscription({ projectId: project.id })
      setDone(res)
      // Let the user read the confirmation, then close + notify the
      // parent so it can patch the project row's plan to Basic
      // without a full page reload.
      setTimeout(() => { onCancelled?.(res); onClose?.() }, 2500)
    } catch (e) {
      const data = e?.response?.data || {}
      setError(data.message || e?.message || 'Cancellation failed')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-800">Cancel Plan</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 text-sm">
          {done ? (
            <div className="flex items-start gap-2 text-green-700">
              <Check className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">Cancellation scheduled.</div>
                <div className="text-gray-700 mt-1">{done.message}</div>
              </div>
            </div>
          ) : (
            <>
              <p className="text-gray-700">
                Cancel the paid plan for <strong>{project.name}</strong>?
              </p>
              <p className="text-gray-600 mt-2">
                The subscription is cancelled immediately and the project
                reverts to <strong>Basic (free)</strong>. Billing stops
                right away — no refund is issued for unused time.
              </p>
              {error && (
                <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>
        {!done && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-3">
            <button onClick={onClose} disabled={busy} className="text-sm text-[#358ECD] hover:underline disabled:opacity-50">Keep Plan</button>
            <button
              type="button"
              onClick={confirm}
              disabled={busy}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm font-semibold rounded inline-flex items-center gap-1"
            >
              {busy && <Loader className="w-3.5 h-3.5 animate-spin" />}
              Cancel Plan
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── "Billing History" modal ─────────────────────────────────────────────────
// Lists the cached Stripe invoices for a project. Each row has a
// "Download PDF" button (uses Stripe's hosted `invoice_pdf` URL) and
// a "Receipt" link (Stripe's hosted receipt page). Empty state fires
// when Stripe has never delivered an invoice event for this project.
function BillingHistoryModal({ open, project, onClose }) {
  const [invoices, setInvoices] = useState([])
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState(null)

  useEffect(() => {
    if (!open || !project?.id) return
    let cancelled = false
    setLoading(true); setError(null)
    projectsApi.fetchSubscriptionInvoices(project.id)
      .then((rows) => { if (!cancelled) setInvoices(rows) })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load invoices') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, project?.id])

  const fmt = (iso) => {
    if (!iso) return ''
    const d = new Date(iso.replace(' ', 'T') + 'Z')
    if (isNaN(d.getTime())) return String(iso)
    try {
      return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d)
    } catch { return d.toDateString() }
  }

  if (!open || !project) return null
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">
            Billing History <span className="text-sm font-normal text-gray-500">— {project.name}</span>
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-auto px-6 py-4">
          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}
          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">
              <Loader className="inline w-4 h-4 animate-spin mr-1" /> Loading…
            </div>
          ) : invoices.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500 italic">
              No invoices yet. New charges will appear here after Stripe processes them.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="text-left py-2 pr-3 font-semibold">Number</th>
                  <th className="text-left py-2 pr-3 font-semibold">Plan</th>
                  <th className="text-left py-2 pr-3 font-semibold">Date</th>
                  <th className="text-left py-2 pr-3 font-semibold">Period</th>
                  <th className="text-right py-2 pr-3 font-semibold">Amount</th>
                  <th className="text-left py-2 pr-3 font-semibold">Status</th>
                  <th className="text-right py-2 font-semibold">Download</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-100">
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700 whitespace-nowrap">{inv.number || inv.stripe_invoice_id}</td>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">
                      {inv.plan_label
                        ? <span className="capitalize">{inv.plan_label}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">{fmt(inv.paid_at)}</td>
                    <td className="py-2 pr-3 text-gray-700 whitespace-nowrap">
                      {inv.period_start && inv.period_end
                        ? `${inv.period_start} → ${inv.period_end}`
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-right text-gray-800">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: inv.currency || 'USD' }).format(inv.amount || 0)}
                    </td>
                    <td className="py-2 pr-3">
                      {inv.status === 'paid'
                        ? <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-green-100 text-green-700">Paid</span>
                        : <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded bg-gray-100 text-gray-700">{inv.status || '—'}</span>}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <button type="button"
                              onClick={() => projectsApi.downloadInvoicePdf(inv.id, inv.number || inv.stripe_invoice_id)}
                              className="text-[#358ECD] hover:underline text-sm">
                        Invoice
                      </button>
                      {inv.pdf_url && (
                        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer"
                           className="text-[#358ECD] hover:underline text-sm ml-3">
                          Stripe PDF
                        </a>
                      )}
                      {inv.hosted_url && (
                        <a href={inv.hosted_url} target="_blank" rel="noopener noreferrer"
                           className="text-[#358ECD] hover:underline text-sm ml-3">
                          Receipt
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex justify-end">
          <button type="button" onClick={onClose}
                  className="px-4 py-1.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── "Change Plan" popup — tier picker + Stripe Checkout redirect ────────────
// Grid of AccountType tiers. Selecting one + Continue → POST to
// /account/subscriptions/checkout/ → redirect the browser to the
// Stripe Checkout URL returned by the server. Cancel just closes.
function ChangePlanModal({ open, project, tiers, onClose }) {
  const currentTier = project?.plan?.name || project?.plan_name || 'Basic'
  const [picked,     setPicked]     = useState(currentTier)
  const [period,     setPeriod]     = useState('monthly')  // 'monthly' | 'annual'
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)

  useEffect(() => {
    if (open) {
      setPicked(currentTier)
      setPeriod(project?.plan?.period || 'monthly')
      setError(null)
    }
  }, [open, currentTier, project?.plan?.period])

  if (!open || !project) return null

  const submit = async () => {
    setSubmitting(true); setError(null)
    try {
      const resp = await projectsApi.startSubscriptionCheckout({
        projectId: project.id, tierName: picked, period,
      })
      if (resp?.url) {
        // Full-page redirect to Stripe-hosted Checkout.
        window.location.assign(resp.url)
        return
      }
      setError('Checkout session was created but no redirect URL was returned. Contact support.')
    } catch (e) {
      const data = e?.response?.data || {}
      setError(
        data.code === 'stripe_not_configured'
          ? 'Stripe is not configured on the server yet. Ops needs to complete docs/stripe-setup.md before Change Plan will work.'
          : data.code === 'price_id_missing'
          ? data.message
          : (data.message || e?.message || 'Failed to start checkout'),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-800">Change Plan</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          <p className="text-xs text-gray-600 mb-3">
            Pick a plan for <span className="font-semibold">{project.name}</span>. You&apos;ll be redirected to
            Stripe to complete payment. Your current plan is <span className="font-semibold">{currentTier}</span>.
          </p>

          {error && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}

          {/* Billing-period toggle. Basic ignores this since both prices
              are 0; the toggle is intentionally always visible so the
              user can see the annual discount before picking a tier. */}
          <div className="flex items-center gap-1 mb-3 bg-gray-100 rounded p-1 w-fit">
            {['monthly', 'annual'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs font-semibold rounded transition capitalize ${
                  period === p ? 'bg-white shadow text-[#358ECD]' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {p}{p === 'annual' ? ' (save ~10%)' : ''}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {tiers.map((t) => {
              const included = (t.features || []).filter((f) => f.included).map((f) => f.label)
              const isCurrent = t.name === currentTier
              const isPicked  = t.name === picked
              const isBasic   = t.name === 'Basic'
              const displayPrice = isBasic
                ? 0
                : (period === 'annual' ? (t.price_annual ?? 0) : (t.price_monthly ?? t.price ?? 0))
              const periodLabel = isBasic ? 'free' : (period === 'annual' ? ' / year' : ' / month')
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setPicked(t.name)}
                  className={`text-left border rounded-lg p-3 transition ${
                    isPicked
                      ? 'border-[#358ECD] bg-[#EBF5FB]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold text-gray-800">{t.name}</span>
                    {isCurrent && (
                      <span className="text-[10px] uppercase tracking-wide text-gray-500">Current</span>
                    )}
                    {isPicked && !isCurrent && (
                      <Check className="w-4 h-4 text-[#358ECD]" />
                    )}
                  </div>
                  <div className="text-lg font-semibold text-gray-800">
                    {isBasic ? 'Free' : fmtMoney(displayPrice)}
                    {!isBasic && <span className="text-xs font-normal text-gray-500">{periodLabel}</span>}
                  </div>
                  <ul className="mt-2 text-xs text-gray-600 space-y-0.5">
                    {included.slice(0, 4).map((f) => <li key={f}>• {f}</li>)}
                    {included.length > 4 && (
                      <li className="text-gray-500 italic">+{included.length - 4} more</li>
                    )}
                  </ul>
                </button>
              )
            })}
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50 rounded-b-lg">
          <button type="button" onClick={onClose} disabled={submitting}
                  className="px-4 py-1.5 text-sm text-gray-700 hover:underline disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || picked === 'Basic' || (picked === currentTier && period === (project?.plan?.period || 'monthly'))}
            className="px-5 py-1.5 bg-[#358ECD] hover:bg-[#2A78B0] text-white text-sm font-semibold rounded disabled:opacity-50 inline-flex items-center gap-1"
            title={picked === 'Basic' ? 'Basic is free — no checkout needed' : (picked === currentTier ? 'Pick a different plan or billing period' : '')}
          >
            {submitting && <Loader className="w-3.5 h-3.5 animate-spin" />}
            Continue to Checkout
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Project plan row (main page) ─────────────────────────────────────────────
function ProjectPlanRow({ project, tiers, onManage, onShowFeatures, onViewBilling }) {
  const tierName = project.plan?.name || project.plan_name || 'Basic'
  const features = featuresFor(tierName, tiers)
  const previewCount = 5
  const preview  = features.slice(0, previewCount)
  const rest     = Math.max(0, features.length - previewCount)

  const billing    = project.billing || {}
  const hasMethod  = !!billing.method_last4
  // Prefer the real Stripe invoice amount; otherwise the computed total
  // (base tier + all add-ons), falling back to the base price alone.
  const nextAmount = billing.next_amount ?? project.plan?.total ?? project.plan?.price
  const nextCurrency = billing.currency || 'USD'
  const cadence    = billing.cadence || project.plan?.cadence || (nextAmount ? 'Monthly' : '')

  // Redirect to Stripe-hosted setup-mode Checkout to add / change a
  // payment method on this project's Stripe customer.
  const [payBusy, setPayBusy] = useState(false)
  const openPaymentSetup = async () => {
    setPayBusy(true)
    try {
      const res = await projectsApi.startPaymentSetup({ projectId: project.id })
      if (res?.url) { window.location.assign(res.url); return }
      alert('Payment setup started but no redirect URL — contact support.')
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || 'Failed to start payment setup.')
    } finally { setPayBusy(false) }
  }

  return (
    <div className="grid grid-cols-3 gap-8 py-6 border-t border-gray-200 first:border-t-0">
      {/* Left — project + plan + Manage Plan */}
      <div>
        <div className="common-size15 text-sm font-bold text-[#4D4D4D]">Project:</div>
        <div className="common-size15 text-base text-gray-800 mb-3">{project.name}</div>
        <div className="common-size15 text-sm font-bold text-[#4D4D4D]">Plan:</div>
        <div className="common-size15 text-base text-gray-800 mb-2">{tierName}</div>
        {Array.isArray(project.plan?.add_ons) && project.plan.add_ons.length > 0 && (
          <div className="mb-3">
            <div className="text-sm font-semibold text-gray-600">Add-Ons:</div>
            <ul className="text-sm text-gray-700 space-y-0.5">
              {project.plan.add_ons.map((a) => (
                <li key={a.id}>
                  {a.label}
                  {a.price ? (
                    <span className="text-gray-500"> – {fmtMoney(a.price, nextCurrency)}/{a.period === 'annual' ? 'yr' : 'mo'}</span>
                  ) : null}
                  {a.status && a.status !== 'active' && (
                    <span className="ml-1 text-[10px] uppercase text-gray-400">({a.status})</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <button
          type="button"
          onClick={() => onManage?.(project)}
          className="text-sm text-[#358ECD] hover:underline"
        >
          Manage Plan
        </button>
      </div>

      {/* Middle — Included in Your Plan */}
      <div>
        <div className="common-size15 text-sm font-bold text-[#4D4D4D] mb-2">Included in Your Plan</div>
        {features.length === 0 ? (
          <p className="common-size15 text-xs text-gray-400 italic">No feature list on file.</p>
        ) : (
          <div className="space-y-1">
            {preview.map((f) => <div key={f} className="text-sm text-gray-700">{f}</div>)}
            {rest > 0 && (
              <button
                type="button"
                onClick={() => onShowFeatures?.({ tierName, features })}
                className="text-sm text-[#358ECD] hover:underline"
              >
                +{rest} View More
              </button>
            )}
          </div>
        )}
      </div>

      {/* Right — Billing and payment */}
      <div>
        <div className="common-size15 text-sm font-bold text-[#4D4D4D] mb-2">Billing and payment</div>
        <div className="flex items-start gap-2 text-sm text-gray-700">
          <CreditCard className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
          <div>
            {hasMethod ? (
              <>
                <div>{billing.method_type || 'Visa'} ending ****{billing.method_last4}</div>
                <button
                  type="button"
                  onClick={openPaymentSetup}
                  disabled={payBusy}
                  className="text-xs text-[#358ECD] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {payBusy ? 'Redirecting…' : 'Change Payment'}
                </button>
              </>
            ) : (
              <>
                <div>No Payment Method</div>
                <button
                  type="button"
                  onClick={openPaymentSetup}
                  disabled={payBusy}
                  className="text-xs text-[#358ECD] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {payBusy ? 'Redirecting…' : 'Add Payment'}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-start gap-2 text-sm text-gray-700 mt-3">
          <Calendar className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
          <div>
            {nextAmount ? (
              <>
                <div>{fmtMoney(nextAmount, nextCurrency)} {cadence}</div>
                {billing.next_payment_at && (
                  <div className="text-xs text-gray-500">Next payment on {fmtDate(billing.next_payment_at)}</div>
                )}
                {project.plan?.billing_note && (
                  <div className="text-xs text-gray-500">{project.plan.billing_note}</div>
                )}
              </>
            ) : (
              <div>FREE</div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onViewBilling?.(project)}
          className="inline-flex items-center gap-1 text-sm text-[#358ECD] hover:underline mt-3"
        >
          View Billing History <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

export default function PlansPage() {
  const { isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [tiers,    setTiers]    = useState([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)

  // Popup state
  const [managing,   setManaging]   = useState(null)  // Project | null
  const [featurePop, setFeaturePop] = useState(null)  // { tierName, features } | null
  const [changing,   setChanging]   = useState(null)  // Project | null
  const [billing,    setBilling]    = useState(null)  // Project | null
  const location = useLocation()
  // Banner on return from Stripe Checkout — success / cancelled / clean.
  // Snapshot the URL param ONCE on mount, then strip it from the URL
  // so a page refresh doesn't keep the banner up forever. Also
  // auto-hide the banner after ~12s so the user isn't stuck with it.
  const [checkoutStatus, setCheckoutStatus] = useState(
    () => new URLSearchParams(location.search).get('checkout') || ''
  )
  // Snapshot the Stripe session id off the URL too — needed by the
  // setup-mode return path to sync the payment method immediately.
  const returnSessionId = useMemo(
    () => new URLSearchParams(location.search).get('session_id') || '',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  useEffect(() => {
    if (!checkoutStatus) return
    // Strip ?checkout=… from the visible URL so a refresh doesn't
    // re-show the banner. Uses history.replaceState directly (React
    // Router's `replace: true` would re-run this effect).
    try {
      const url = new URL(window.location.href)
      url.searchParams.delete('checkout')
      url.searchParams.delete('session_id')
      window.history.replaceState({}, '', url.toString())
    } catch { /* ignore */ }
    const t = setTimeout(() => setCheckoutStatus(''), 12_000)
    return () => clearTimeout(t)
  }, [checkoutStatus])

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    Promise.all([
      projectsApi.fetchProjects(1, 100),
      projectsApi.fetchTiers(),
    ])
      .then(([res, ts]) => {
        if (cancelled) return
        const list = res?.success && Array.isArray(res.data) ? res.data : []
        setProjects(list)
        // fetchTiers now returns the full response { tiers, cellular_plans }
        // — extract the array shape the rest of this page expects.
        setTiers(Array.isArray(ts?.tiers) ? ts.tiers : (Array.isArray(ts) ? ts : []))
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load plans') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // On return from a successful Stripe Checkout redirect, poll each
  // project's subscription status until the webhook lands and updates
  // the row. Poll 5× at 2s intervals — Stripe usually delivers within
  // a couple of seconds. Any missed updates fall through to the next
  // page load anyway.
  useEffect(() => {
    if (checkoutStatus !== 'success' || projects.length === 0) return
    let attempts = 0, cancelled = false
    const tick = async () => {
      if (cancelled || attempts >= 5) return
      attempts++
      try {
        const results = await Promise.all(
          projects.map((p) => projectsApi.fetchSubscriptionStatus(p.id).catch(() => null)),
        )
        // Merge status → plan.name onto each project so the middle card
        // renders the new tier immediately without a hard reload.
        setProjects((prev) => prev.map((p, i) => {
          const s = results[i]
          if (!s?.status || s.status === '') return p
          const tier = tiers.find((t) => t.id === s.account_type_id)
          return tier ? { ...p, plan: { ...(p.plan || {}), name: tier.name } } : p
        }))
      } catch { /* keep polling */ }
      setTimeout(tick, 2000)
    }
    tick()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, projects.length, tiers.length])

  // On return from a setup-mode Checkout (Add Payment), ask the
  // backend to sync the customer's default payment method right away
  // so the "Visa ending 4242" line appears without waiting for
  // Stripe's webhook. Fires ONCE across all projects because setup
  // mode doesn't say which project it was for beyond the session id.
  useEffect(() => {
    if (checkoutStatus !== 'success' || !returnSessionId || projects.length === 0) return
    let cancelled = false
    ;(async () => {
      // Try each project's refresh endpoint until one succeeds — the
      // backend uses the session_id to resolve the right project.
      for (const p of projects) {
        if (cancelled) return
        try {
          const r = await projectsApi.refreshPaymentMethod({
            projectId: p.id,
            sessionId: returnSessionId,
          })
          if (r?.method_last4) {
            if (cancelled) return
            setProjects((prev) => prev.map((pp) =>
              pp.id === p.id
                ? { ...pp, billing: {
                      ...(pp.billing || {}),
                      method_type:  r.method_type  || '',
                      method_last4: r.method_last4 || '',
                    } }
                : pp,
            ))
            return
          }
        } catch { /* try next project */ }
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, returnSessionId, projects.length])

  return (
    <>
      <Navigation />
      {isAuthenticated && <AppHeader />}
      <div className="min-h-screen bg-gray-100">
        <div className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4 p-6 border-b">
                <h1 className="text-lg font-bold text-gray-800">Your Project Plans</h1>
                <button
                  type="button"
                  onClick={() => navigate('/account')}
                  className="text-sm text-[#358ECD] hover:underline"
                >
                  ← Back to Account
                </button>
              </div>

              {error && (
                <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> {error}
                </div>
              )}
              {checkoutStatus === 'success' && (
                <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded text-sm text-green-800 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Payment received. Your new plan activates as soon as Stripe confirms the subscription
                  (usually within a few seconds). Refresh in a moment if the plan hasn&apos;t updated yet.
                </div>
              )}
              {checkoutStatus === 'cancelled' && (
                <div className="mb-4 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" /> Checkout was cancelled. Your plan hasn&apos;t changed.
                </div>
              )}

              {loading ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  <Loader className="inline w-4 h-4 animate-spin mr-1" /> Loading plans…
                </div>
              ) : projects.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500 italic">
                  No projects on this account yet.
                </div>
              ) : (
                <div className="px-6">
                  {projects.map((p) => (
                    <ProjectPlanRow
                      key={p.id}
                      project={p}
                      tiers={tiers}
                      onManage={setManaging}
                      onShowFeatures={setFeaturePop}
                      onViewBilling={setBilling}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ManagePlanModal
        open={!!managing}
        project={managing}
        tiers={tiers}
        onClose={() => setManaging(null)}
        onShowFeatures={setFeaturePop}
        onChangePlan={(p) => { setManaging(null); setChanging(p) }}
        onPlanCancelled={(projId, payload) => {
          // Patch the project row locally so the middle card flips to
          // Basic + FREE without a manual reload. Backend has already
          // reset the ProjectSubscription; the next full refresh will
          // agree.
          setProjects((prev) => prev.map((p) => (
            p.id === projId
              ? { ...p, plan: {
                    ...(p.plan || {}),
                    name:                 payload?.plan_name || 'Basic',
                    status:               payload?.status    || 'canceled',
                    period:               '',
                    price:                0,
                    cancel_at_period_end: false,
                  } }
              : p
          )))
        }}
      />
      <ChangePlanModal
        open={!!changing}
        project={changing}
        tiers={tiers}
        onClose={() => setChanging(null)}
      />
      <IncludedFeaturesModal
        open={!!featurePop}
        tierName={featurePop?.tierName}
        features={featurePop?.features}
        onClose={() => setFeaturePop(null)}
      />
      <BillingHistoryModal
        open={!!billing}
        project={billing}
        onClose={() => setBilling(null)}
      />
    </>
  )
}
